<script setup lang="ts">
import { useMediaGrid } from './useMediaGrid';
import {
  DEFAULT_COLUMNS,
  useResponsiveMediaColumns,
} from './useResponsiveMediaColumns';
import type { ColumnsConfig, MediaItem } from './types';
import { resizeImage, resizeImageSrcsetWidths } from '~/shared/lib/resizeImage';
import { MEASURE_IMAGE_WIDTH } from './useImageRatios';

const props = withDefaults(
  defineProps<{
    items: MediaItem[];
    columns?: ColumnsConfig;
  }>(),
  {
    columns: () => DEFAULT_COLUMNS,
  },
);

const emit = defineEmits<{
  'item-click': [item: MediaItem, index: number];
}>();

defineOptions({
  name: 'AdaptiveMediaGrid',
});

const IMAGE_WIDTHS = [MEASURE_IMAGE_WIDTH, 360, 480, 720, 960, 1440];

// Max grid width on desktop, see EventsGalleryDetailLayout
const DESKTOP_GRID_WIDTH = 860;

// A tile spans two rows: 125px on desktop, at most 145px below
const DESKTOP_TILE_HEIGHT = 260;
const MAX_TILE_HEIGHT = 300;

const SHORT_GALLERY_COLUMNS: ColumnsConfig = {
  mobile: 1,
  tablet: 2,
  desktop: 2,
};

const mediaItems = toRef(props, 'items');
const isShortGallery = computed(() => mediaItems.value.length <= 2);

const effectiveColumns = computed<ColumnsConfig>(() =>
  isShortGallery.value ? SHORT_GALLERY_COLUMNS : props.columns,
);
const { columnsCount } = useResponsiveMediaColumns(effectiveColumns);

const gridStyle = computed(() => ({
  '--adaptive-media-grid-columns': String(columnsCount.value),
}));

const { isGridReady, layoutItems } = useMediaGrid(
  mediaItems,
  columnsCount,
  isShortGallery,
);

/*
  Tiles crop with object-fit: cover, so a landscape photo in a tall tile is
  sized by the tile height, not its width. Ask for whichever is larger.
*/
function getImageSizes(isWide: boolean, ratio = 1): string {
  const share = (isWide ? 2 : 1) / columnsCount.value;
  const desktopWidth = Math.max(
    DESKTOP_GRID_WIDTH * share,
    DESKTOP_TILE_HEIGHT * ratio,
  );
  const minWidth = Math.round(MAX_TILE_HEIGHT * ratio);
  // Viewport where the tile width outgrows the height-driven width
  const crossover = Math.ceil(minWidth / share);

  return [
    `(min-width: 1380px) ${Math.round(desktopWidth)}px`,
    crossover < 1380
      ? `(min-width: ${crossover}px) ${Math.round(100 * share)}vw`
      : null,
    `${minWidth}px`,
  ]
    .filter(Boolean)
    .join(', ');
}

function handleItemClick(item: MediaItem, index: number): void {
  emit('item-click', item, index);
}
</script>

<template>
  <div v-if="isGridReady" :class="$style.grid" :style="gridStyle">
    <button
      v-for="{ item, index, isWide, ratio } in layoutItems"
      :key="item.id"
      type="button"
      :class="[$style.item, { [$style.item_wide]: isWide }]"
      :aria-label="item.alt ?? ''"
      @click="handleItemClick(item, index)"
    >
      <img
        v-if="item.src"
        :src="resizeImage(item.src, { width: MEASURE_IMAGE_WIDTH })"
        :srcset="resizeImageSrcsetWidths(item.src, IMAGE_WIDTHS) || undefined"
        :sizes="getImageSizes(isWide, ratio)"
        :alt="item.alt ?? ''"
        :class="$style.itemImage"
        loading="lazy"
      />
      <slot v-else name="placeholder" :item="item" :index="index" />
      <slot name="overlay" :item="item" :index="index" />
    </button>
  </div>
  <Loader v-else center small :class="$style.loading" />
</template>

<style module lang="scss">
.grid {
  display: grid;
  grid-template-columns: repeat(
    var(--adaptive-media-grid-columns, 4),
    minmax(0, 1fr)
  );
  grid-auto-rows: 125px;
  grid-auto-flow: dense;
  gap: 10px;
}

.loading {
  min-height: 520px;
}

.item {
  position: relative;
  display: block;
  min-width: 0;
  padding: 0;
  margin: 0;
  overflow: hidden;
  background-color: var(--color-gray-bg);
  border: none;
  cursor: pointer;
  color: inherit;
  font: inherit;
  grid-row: span 2;
}

.item_wide {
  grid-column: span 2;
}

.itemImage {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

@media (max-width: 1379px) {
  .grid {
    grid-auto-rows: 145px;
  }

  .loading {
    min-height: 610px;
  }
}

@media (max-width: 767px) {
  .grid {
    grid-auto-rows: 125px;
  }

  .loading {
    min-height: 520px;
  }
}

@media (max-width: 599px) {
  .grid {
    grid-auto-rows: 90px;
  }

  .loading {
    min-height: 380px;
  }
}

@media (max-width: 479px) {
  .grid {
    grid-auto-rows: 105px;
  }
}
</style>
